// src/services/exerciseService.ts
// Exercise data loading + JSON normalization + scoring helpers.
//
// Pure where possible. A small module-scope cache holds the parsed `bookData`
// and `unitMedia` map across unit loads (mirrors the per-instance fields the
// old ExerciseManager kept on `this`). Cleared via clearBookCache().
//
// Network calls are limited to:
//   1. getDoc(/books/{bookId}) — Firestore book metadata
//   2. fetch(meta.exercisesJsonUrl) — the JSON file from Firebase Storage
// Both are retried on the next call only after clearBookCache().

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type {
  BookData,
  BookMeta,
  Exercise,
  ExerciseUserInput,
  GradeResult,
  RawUnitJSON,
  Section,
  UnitData,
} from '../types/exercise';

// ── Module-scope cache ───────────────────────────────────────────────────────

let _cachedBookData: BookData | null = null;
let _cachedBookId: string | null = null;
let _cachedUnitMedia: Record<string, { audioUrl?: string; videoUrl?: string }> = {};

/** Forget the cached book data. Call on logout or when admin re-uploads JSON. */
export function clearBookCache(): void {
  _cachedBookData = null;
  _cachedBookId = null;
  _cachedUnitMedia = {};
}

// ── Data loading ─────────────────────────────────────────────────────────────

export interface LoadBookResult {
  bookData: BookData;
  unitMedia: Record<string, { audioUrl?: string; videoUrl?: string }>;
}

/**
 * Loads /books/{bookId} from Firestore, then fetches the exercises JSON
 * referenced in `exercisesJsonUrl`. Caches both at module scope so the next
 * unit load in the same book skips the network entirely.
 *
 * Cache invalidates automatically when called with a different bookId.
 *
 * Throws if the book doc is missing, if `exercisesJsonUrl` isn't set, or if
 * the JSON fetch fails. Callers should surface the error to the user.
 */
export async function loadBookData(bookId: string): Promise<LoadBookResult> {
  if (!bookId) throw new Error('loadBookData: bookId is required');

  if (_cachedBookId === bookId && _cachedBookData) {
    return { bookData: _cachedBookData, unitMedia: _cachedUnitMedia };
  }

  // Firestore /books/{bookId}
  const snap = await getDoc(doc(db, 'books', bookId));
  if (!snap.exists()) {
    throw new Error(`Book ${bookId} not found in Firestore`);
  }
  const meta = snap.data() as BookMeta;

  if (!meta.exercisesJsonUrl) {
    throw new Error(`Book ${bookId} has no exercisesJsonUrl set`);
  }

  // Fetch the JSON. URL is a Firebase Storage signed/public link served via
  // the SW's CacheFirst route on repeat visits.
  const resp = await fetch(meta.exercisesJsonUrl);
  if (!resp.ok) {
    throw new Error(`Failed to fetch exercises JSON: ${resp.status} ${resp.statusText}`);
  }
  const bookData = (await resp.json()) as BookData;

  if (!bookData?.units || typeof bookData.units !== 'object') {
    throw new Error('Exercises JSON is missing the `units` map');
  }

  _cachedBookData = bookData;
  _cachedBookId = bookId;
  _cachedUnitMedia = meta.unitMedia ?? {};

  return { bookData, unitMedia: _cachedUnitMedia };
}

// ── Unit extraction ──────────────────────────────────────────────────────────

/**
 * Pulls a single unit's raw JSON out of `bookData.units`. Handles BOTH the
 * wrapped (`units["1"].unit`) and flat (`units["1"]`) shapes — admin-panel
 * exports have used both forms over time.
 *
 * `unitNumOrId` accepts '1' / 1 / 'unit1' — strips the prefix automatically.
 */
export function extractUnitData(
  bookData: BookData,
  unitNumOrId: string | number,
): RawUnitJSON | null {
  const key = String(unitNumOrId).replace(/^unit/, '');
  const entry = bookData.units?.[key];
  if (!entry) return null;
  const inner = (entry.unit ?? entry) as RawUnitJSON;
  if (!inner || typeof inner !== 'object') return null;
  return inner;
}

// ── JSON → typed Section[] (the critical port from exercise-manager.js) ─────

/**
 * Builds the 6 fixed sections with typed Exercise objects.
 *
 *   sections[0] reading1   ← readings[0].exercises
 *   sections[1] vocabulary ← vocabulary_exercises
 *   sections[2] grammar    ← grammar_exercises
 *   sections[3] video      ← listening_activities[*].exercises
 *   sections[4] reading2   ← readings[1].exercises
 *   sections[5] audio      ← first 3 of section 3 (video)
 *
 * The raw JSON uses one shape per (section × type) pair. Some shapes get
 * remapped at this layer:
 *   • `comprehension`         → writing_comprehension
 *   • `error_correction`      → writing_comprehension
 *   • `sentence_rewriting`    → writing_comprehension
 *   • `categorization`        → many `multiple_choice` (one per word in word_bank)
 *
 * The `audio` section is just a slice of `video` — both pull from
 * listening_activities, the audio section uses the first 3 only.
 */
export function createSectionsFromJSON(unitData: RawUnitJSON): Section[] {
  const sections: Section[] = [
    { id: 'reading1',   title: 'Reading 1',           icon: 'fas fa-book',       description: 'Comprehension and analysis exercises', exercises: [] },
    { id: 'vocabulary', title: 'Vocabulary',          icon: 'fas fa-language',   description: 'Word matching and usage exercises',    exercises: [] },
    { id: 'grammar',    title: 'Grammar in Practice', icon: 'fas fa-pencil-alt', description: 'Grammar structure exercises',          exercises: [] },
    { id: 'video',      title: 'Video Listening',     icon: 'fas fa-video',      description: 'Video comprehension exercises',        exercises: [] },
    { id: 'reading2',   title: 'Reading 2',           icon: 'fas fa-book-open',  description: 'Advanced reading exercises',           exercises: [] },
    { id: 'audio',      title: 'Audio Listening',     icon: 'fas fa-headphones', description: 'Audio comprehension exercises',        exercises: [] },
  ];

  // ── Reading 1 + Reading 2 ──────────────────────────────────────────────────
  const readings = unitData.readings;
  if (Array.isArray(readings) && readings.length > 0) {
    // Reading 1 → sections[0]
    if (readings[0]?.exercises) {
      readings[0].exercises.forEach((ex) => {
        if (ex.type === 'true_false' && Array.isArray(ex.questions)) {
          const questions = ex.questions as string[];
          const answers = (ex.answers as Array<{ correct?: boolean; correction?: string }> | undefined) ?? [];
          questions.forEach((q, i) => {
            sections[0].exercises.push({
              type: 'true_false',
              question: typeof q === 'string' ? q.replace(/ T\/F/g, '') : String(q),
              correct_answer: answers[i]?.correct ?? false,
              explanation: answers[i]?.correction || 'Refer to the text for explanation.',
            });
          });
        } else if (ex.type === 'comprehension' && Array.isArray(ex.questions)) {
          const questions = ex.questions as string[];
          const answers = (ex.answers as string[] | undefined) ?? [];
          questions.forEach((q, i) => {
            sections[0].exercises.push({
              type: 'writing_comprehension',
              question: q,
              model_answer: answers[i] || 'Refer to the reading text for the correct answer.',
            });
          });
        }
      });
    }

    // Reading 2 → sections[4]
    if (readings[1]?.exercises) {
      readings[1].exercises.forEach((ex) => {
        if (ex.type === 'true_false' && Array.isArray(ex.questions)) {
          const questions = ex.questions as string[];
          const answers = (ex.answers as Array<{ correct?: boolean; correction?: string }> | undefined) ?? [];
          questions.forEach((q, i) => {
            sections[4].exercises.push({
              type: 'true_false',
              question: typeof q === 'string' ? q.replace(/ T\/F/g, '') : String(q),
              correct_answer: answers[i]?.correct ?? false,
              explanation: answers[i]?.correction || 'Refer to the text for explanation.',
            });
          });
        } else if (ex.type === 'matching' && Array.isArray(ex.paragraphs_to_match)) {
          const items = ex.paragraphs_to_match as Array<{ paragraph: string; correct: string }>;
          const headings = (ex.headings as string[] | undefined) ?? [];
          sections[4].exercises.push({
            type: 'matching',
            question:
              typeof ex.instructions === 'string'
                ? ex.instructions
                : 'Match each paragraph to its most appropriate title:',
            pairs: items.map((item) => ({
              word: item.paragraph,
              definition: getMatchingDefinition(item.correct, headings),
            })),
          });
        }
      });
    }
  }

  // ── Vocabulary → sections[1] ───────────────────────────────────────────────
  if (Array.isArray(unitData.vocabulary_exercises)) {
    unitData.vocabulary_exercises.forEach((ex) => {
      if (ex.type === 'matching' && Array.isArray(ex.pairs)) {
        const pairs = ex.pairs as Array<{ word: string; definition: unknown }>;
        sections[1].exercises.push({
          type: 'matching',
          question:
            typeof ex.instructions === 'string'
              ? ex.instructions
              : 'Match the words with their correct definitions:',
          pairs: pairs.map((p) => ({
            word: p.word,
            definition:
              typeof p.definition === 'string'
                ? p.definition.replace(/^[A-Z]\.\s*/, '')
                : String(p.definition),
          })),
        });
      } else if (ex.type === 'fill_in_blank' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{ sentence: string; correct: string }>;
        const wordBox = (ex.word_box as string[] | undefined) ?? [];
        const instructions =
          typeof ex.instructions === 'string' ? ex.instructions : 'Complete the sentence:';
        sentences.forEach((s) => {
          sections[1].exercises.push({
            type: 'fill_blank',
            question: `${instructions} "${s.sentence}"`,
            correct_answer: s.correct,
            options: wordBox,
          });
        });
      } else if (ex.type === 'comprehension' && Array.isArray(ex.questions)) {
        const qs = ex.questions as Array<{ question: string; answer: string }>;
        qs.forEach((q) => {
          sections[1].exercises.push({
            type: 'writing_comprehension',
            question: q.question,
            model_answer: q.answer,
          });
        });
      } else if (ex.type === 'true_false_correction' && Array.isArray(ex.statements)) {
        const stmts = ex.statements as Array<{
          statement: string;
          correct: boolean;
          correction?: string;
        }>;
        stmts.forEach((s) => {
          sections[1].exercises.push({
            type: 'true_false_correction',
            question: s.statement,
            correct_answer: s.correct,
            explanation: s.correction || '',
          });
        });
      } else if (ex.type === 'sentence_rearrangement' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{ original: string; correct: string }>;
        sentences.forEach((s) => {
          sections[1].exercises.push({
            type: 'sentence_rearrangement',
            question: `Rearrange: "${s.original}"`,
            correct_answer: s.correct,
          });
        });
      } else if (ex.type === 'multiple_choice' && Array.isArray(ex.questions)) {
        // Vocab MC uses 'questions' (different from grammar which uses 'sentences')
        const qs = ex.questions as Array<{ question: string; options: string[]; correct: number }>;
        qs.forEach((q) => {
          sections[1].exercises.push({
            type: 'multiple_choice',
            question: q.question,
            options: q.options,
            correct_answer: q.correct,
          });
        });
      } else if (
        ex.type === 'categorization' &&
        ex.categories &&
        typeof ex.categories === 'object'
      ) {
        // Categorization expands into ONE multiple_choice exercise PER word.
        const categories = ex.categories as Record<string, string[]>;
        const categoryNames = Object.keys(categories);
        const wordBank = (ex.word_bank as string[] | undefined) ?? [];
        const wordToCategory: Record<string, string> = {};
        categoryNames.forEach((cat) => {
          (categories[cat] || []).forEach((w) => {
            wordToCategory[w] = cat;
          });
        });
        wordBank.forEach((word) => {
          if (!wordToCategory[word]) return;
          sections[1].exercises.push({
            type: 'multiple_choice',
            question: `Which category does "<strong>${word}</strong>" belong to?`,
            options: categoryNames,
            correct_answer: categoryNames.indexOf(wordToCategory[word]),
          });
        });
      }
    });
  }

  // ── Grammar → sections[2] ──────────────────────────────────────────────────
  if (Array.isArray(unitData.grammar_exercises)) {
    unitData.grammar_exercises.forEach((ex) => {
      if (ex.type === 'multiple_choice' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{
          sentence: string;
          options: string[];
          correct: number;
        }>;
        sentences.forEach((s) => {
          sections[2].exercises.push({
            type: 'multiple_choice',
            question: s.sentence,
            options: s.options,
            correct_answer: s.correct,
          });
        });
      } else if (ex.type === 'fill_in_blank' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{ sentence: string; correct: string }>;
        sentences.forEach((s) => {
          sections[2].exercises.push({
            type: 'fill_blank',
            question: s.sentence.replace('---', '______'),
            correct_answer: s.correct,
          });
        });
      } else if (ex.type === 'error_correction' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{ original: string; corrected: string }>;
        sentences.forEach((s) => {
          sections[2].exercises.push({
            type: 'writing_comprehension',
            question: `Correct this sentence: "${s.original}"`,
            model_answer: s.corrected,
          });
        });
      } else if (ex.type === 'sentence_rewriting' && Array.isArray(ex.sentences)) {
        const sentences = ex.sentences as Array<{ original: string; corrected: string }>;
        sentences.forEach((s) => {
          sections[2].exercises.push({
            type: 'writing_comprehension',
            question: `Rewrite this sentence correctly: "${s.original}"`,
            model_answer: s.corrected,
          });
        });
      }
    });
  }

  // ── Video / Listening → sections[3] ────────────────────────────────────────
  if (Array.isArray(unitData.listening_activities)) {
    unitData.listening_activities.forEach((activity) => {
      if (!activity?.exercises) return;
      activity.exercises.forEach((ex) => {
        if (ex.type === 'true_false' && Array.isArray(ex.questions)) {
          const qs = ex.questions as Array<{ question: string; correct: boolean }>;
          qs.forEach((q) => {
            sections[3].exercises.push({
              type: 'true_false',
              question: q.question,
              correct_answer: q.correct,
              explanation: 'Based on the listening content',
            });
          });
        } else if (ex.type === 'fill_in_blank' && Array.isArray(ex.blanks)) {
          const blanks = ex.blanks as Array<{ sentence: string; answer: string }>;
          blanks.forEach((b) => {
            sections[3].exercises.push({
              type: 'fill_blank',
              question: b.sentence.replace('______', '__________'),
              correct_answer: b.answer,
            });
          });
        }
      });
    });
  }

  // ── Audio = first 3 of Video → sections[5] ─────────────────────────────────
  if (sections[3].exercises.length > 0) {
    sections[5].exercises = sections[3].exercises.slice(0, 3);
  }

  return sections;
}

function getMatchingDefinition(correctLetter: string, headings: string[]): string {
  const heading = headings.find(
    (h) => typeof h === 'string' && h.startsWith(correctLetter + '.'),
  );
  return heading ? heading.substring(3) : 'Definition not found';
}

// ── High-level helper ────────────────────────────────────────────────────────

export interface GetUnitResult {
  unitData: UnitData;
  /** May be undefined if no media has been uploaded for this unit yet. */
  unitMedia: { audioUrl?: string; videoUrl?: string } | undefined;
}

/**
 * One-shot loader: load the book + extract a unit + convert to typed Section[].
 * Caller does:
 *   const { unitData, unitMedia } = await getUnitData(bookId, 'unit3');
 */
export async function getUnitData(bookId: string, unitId: string): Promise<GetUnitResult> {
  const { bookData, unitMedia } = await loadBookData(bookId);
  const raw = extractUnitData(bookData, unitId);
  if (!raw) {
    throw new Error(`Unit ${unitId} not found in book ${bookId}`);
  }
  return {
    unitData: {
      title: raw.title || `${unitId.toUpperCase()} - Exercises`,
      subtitle: raw.subtitle || 'Interactive Learning Materials',
      sections: createSectionsFromJSON(raw),
    },
    unitMedia: unitMedia[unitId],
  };
}

// ── Scoring ──────────────────────────────────────────────────────────────────

/** Maximum points obtainable on this exercise. */
export function calculateMaxScore(exercise: Exercise): number {
  switch (exercise.type) {
    case 'matching':
    case 'categorization':
      return exercise.pairs?.length ?? 1;
    case 'true_false':
    case 'true_false_correction':
    case 'multiple_choice':
    case 'fill_blank':
    case 'writing_comprehension':
    case 'sentence_rearrangement':
    default:
      return 1;
  }
}

/**
 * Pure scoring. Takes an exercise + the user's typed input, returns the grade.
 * No DOM access. UI components track input in component state and pass the
 * typed value here when the user clicks Submit.
 *
 * `writing_comprehension` is always graded as correct — the model answer is
 * shown for self-evaluation, matching the old behavior.
 */
export function gradeExercise(exercise: Exercise, input: ExerciseUserInput): GradeResult {
  const maxScore = calculateMaxScore(exercise);

  switch (exercise.type) {
    case 'true_false':
    case 'true_false_correction': {
      const value =
        input.type === 'true_false' || input.type === 'true_false_correction'
          ? input.value
          : null;
      const isCorrect = value === exercise.correct_answer;
      return {
        isCorrect,
        scoreEarned: isCorrect ? 1 : 0,
        maxScore,
        userAnswerDisplay: value === null ? 'No answer' : value ? 'True' : 'False',
        correctAnswerDisplay: exercise.correct_answer ? 'True' : 'False',
      };
    }

    case 'multiple_choice': {
      const userIdx = input.type === 'multiple_choice' ? input.selectedIndex : null;
      const isCorrect = userIdx !== null && userIdx === exercise.correct_answer;
      const opts = exercise.options ?? [];
      return {
        isCorrect,
        scoreEarned: isCorrect ? 1 : 0,
        maxScore,
        userAnswerDisplay:
          userIdx !== null && opts[userIdx] !== undefined ? opts[userIdx] : 'No answer',
        correctAnswerDisplay:
          typeof exercise.correct_answer === 'number' && opts[exercise.correct_answer] !== undefined
            ? opts[exercise.correct_answer]
            : String(exercise.correct_answer ?? ''),
      };
    }

    case 'fill_blank': {
      const value = input.type === 'fill_blank' ? input.value.trim() : '';
      const correct = String(exercise.correct_answer ?? '');
      const isCorrect = value.length > 0 && value.toLowerCase() === correct.toLowerCase();
      return {
        isCorrect,
        scoreEarned: isCorrect ? 1 : 0,
        maxScore,
        userAnswerDisplay: value || 'No answer',
        correctAnswerDisplay: correct,
      };
    }

    case 'writing_comprehension': {
      const value = input.type === 'writing_comprehension' ? input.value.trim() : '';
      return {
        isCorrect: true,
        scoreEarned: 1,
        maxScore,
        userAnswerDisplay: value || '(written answer)',
        correctAnswerDisplay: exercise.model_answer ?? '',
      };
    }

    case 'matching': {
      const selections = input.type === 'matching' ? input.selections : {};
      const pairs = exercise.pairs ?? [];
      let correct = 0;
      pairs.forEach((p) => {
        if (selections[p.word] === p.definition) correct++;
      });
      const isCorrect = pairs.length > 0 && correct === pairs.length;
      return {
        isCorrect,
        scoreEarned: correct,
        maxScore,
        userAnswerDisplay: `${correct}/${pairs.length} matched`,
        correctAnswerDisplay: `All ${pairs.length} pairs`,
      };
    }

    case 'sentence_rearrangement': {
      const value = input.type === 'sentence_rearrangement' ? input.value.trim() : '';
      const correct = String(exercise.correct_answer ?? '').trim();
      const isCorrect = value.length > 0 && value.toLowerCase() === correct.toLowerCase();
      return {
        isCorrect,
        scoreEarned: isCorrect ? 1 : 0,
        maxScore,
        userAnswerDisplay: value || 'No answer',
        correctAnswerDisplay: correct,
      };
    }

    case 'categorization': {
      // DEAD-CODE BRANCH: createSectionsFromJSON converts categorization JSON
      // into many `multiple_choice` exercises, so this case typically does not
      // fire. Kept as a defensive 1:1 port — if a future admin export ships
      // raw `categorization` exercises, they'll grade correctly.
      const selections = input.type === 'categorization' ? input.selections : {};
      const pairs = exercise.pairs ?? [];
      let correct = 0;
      pairs.forEach((p) => {
        if (selections[p.word] === p.definition) correct++;
      });
      const isCorrect = pairs.length > 0 && correct === pairs.length;
      return {
        isCorrect,
        scoreEarned: correct,
        maxScore,
        userAnswerDisplay: `${correct}/${pairs.length} correct`,
        correctAnswerDisplay: `All ${pairs.length} words`,
      };
    }
  }

  // Exhaustiveness guard — TS proves all ExerciseType values are handled above.
  const _exhaustive: never = exercise.type;
  void _exhaustive;
  return {
    isCorrect: false,
    scoreEarned: 0,
    maxScore: 1,
    userAnswerDisplay: 'No answer',
    correctAnswerDisplay: '',
  };
}

// ── Lookup helpers ───────────────────────────────────────────────────────────

export function findSection(unit: UnitData, sectionId: string): Section | undefined {
  return unit.sections.find((s) => s.id === sectionId);
}

export function findExercise(
  unit: UnitData,
  sectionId: string,
  index: number,
): Exercise | undefined {
  return findSection(unit, sectionId)?.exercises[index];
}